import { useEffect, useRef } from 'react';

import { CloseIcon } from '@braskai/icons';
import { useDeviceType } from '@braskai/ui-kit';
import { useNavigate } from 'react-router-dom';

import { alpha, Drawer } from '@mui/material';

import { useHasUnlimitedRecordingMinutes } from '@/entities/user/currentUser';

import { PATHS } from '@/shared/constants/routes/paths';
import { IS_WHITE_LABEL } from '@/shared/environments';
import { useInterfaceLanguage } from '@/shared/i18n';
import { enqueueAppSnackbar } from '@/shared/lib/snackbar';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';
import { AlertType } from '@/shared/types/Alert';

import { useRecordingShareAutoFetch } from '../model/useEditorShareAutoFetch';
import { useRecordingShareModalStore } from '../model/useRecordingShareModalStore';
import {
  SBody,
  SCard,
  SCloseButton,
  SCopyButton,
  SDescription,
  SDialog,
  SLinkField,
  SLinkInput,
  SSuccessBadge,
  STitle,
  SUpsellButton,
  SUpsellDivider,
  SUpsellRow,
  SUpsellText,
} from './styled';

type RecordingShareModalInnerProps = {
  copyLink: string;
  isAutoOpened: boolean;
  isOpen: boolean;
  recordingId: null | string;
  showUpsell: boolean;
  onClose: () => void;
  onExited: () => void;
  onUpgrade: () => void;
};

const RecordingShareModalInner = ({
  copyLink,
  isAutoOpened,
  isOpen,
  recordingId,
  showUpsell,
  onClose,
  onExited,
  onUpgrade,
}: RecordingShareModalInnerProps) => {
  const { isMobile } = useDeviceType();
  const { t } = useInterfaceLanguage();

  useEffect(() => {
    if (isOpen && recordingId) {
      MixpanelEvents.recordingShareModalShown({ recording_id: recordingId, recordingId });
    }
  }, [isOpen, recordingId]);

  const handleModalClose = (_event: unknown, reason?: 'backdropClick' | 'escapeKeyDown') => {
    if (reason === 'backdropClick' && isAutoOpened) return;

    onClose();
  };

  const handleCopyLink = async () => {
    if (recordingId) {
      MixpanelEvents.recordingShareCopyLinkClick({ recording_id: recordingId, recordingId });
    }
    try {
      await navigator.clipboard.writeText(copyLink);
      enqueueAppSnackbar(t('recordingModals.share.linkCopied'), { variant: AlertType.Success });
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  const content = (
    <SCard $isMobile={isMobile} data-testid="recording-share-modal">
      <SBody $isMobile={isMobile}>
        <SCloseButton
          aria-label={t('recordingModals.share.closeAria')}
          data-testid="recording-share-modal-close-share-modal"
          type="button"
          onClick={onClose}
        >
          <CloseIcon height={18} width={18} />
        </SCloseButton>

        <SSuccessBadge>
          <svg fill="none" height={22} viewBox="0 0 24 24" width={22}>
            <path
              d="M5 12.5l4.5 4.5L19 7"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2.2"
            />
          </svg>
        </SSuccessBadge>

        <STitle>{t('recordingModals.share.title')}</STitle>
        <SDescription>{t('recordingModals.share.description')}</SDescription>

        <SLinkField>
          <SLinkInput aria-label={t('recordingModals.share.linkAria')} value={copyLink} readOnly />
          <SCopyButton data-testid="recording-share-modal-copy-link" variant="outlined" onClick={handleCopyLink}>
            <svg fill="none" height={15} viewBox="0 0 24 24" width={15}>
              <rect height="11" rx="2.5" stroke="currentColor" strokeWidth="1.8" width="11" x="9" y="9" />
              <path d="M5 15V6a2 2 0 0 1 2-2h9" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
            </svg>
            <span>{t('recordingModals.share.copy')}</span>
          </SCopyButton>
        </SLinkField>
      </SBody>

      {showUpsell && (
        <>
          <SUpsellDivider />
          <SUpsellRow $isMobile={isMobile} data-testid="recording-share-modal-upsell">
            <svg aria-hidden="true" fill="none" height={17} style={{ flexShrink: 0 }} viewBox="0 0 24 24" width={17}>
              <path d="M6 3h12l3 5-9 13L3 8l3-5z" stroke="currentColor" strokeLinejoin="round" strokeWidth="1.6" />
              <path d="M3 8h18M9 3l-1.5 5L12 21l4.5-13L15 3" stroke="currentColor" strokeLinejoin="round" strokeWidth="1.6" />
            </svg>
            <SUpsellText>{t('recordingModals.share.upsell')}</SUpsellText>
            <SUpsellButton data-testid="recording-share-modal-upgrade" type="button" onClick={onUpgrade}>
              {t('common.upgrade')}
            </SUpsellButton>
          </SUpsellRow>
        </>
      )}
    </SCard>
  );

  if (isMobile) {
    return (
      <Drawer
        PaperProps={{
          sx: {
            backgroundColor: 'transparent',
            borderTopLeftRadius: 18,
            borderTopRightRadius: 18,
            boxShadow: 'none',
          },
        }}
        sx={(theme) => ({
          '& .MuiBackdrop-root': { backdropFilter: 'blur(3px)', backgroundColor: alpha(theme.palette.grey[900], 0.45) },
        })}
        anchor="bottom"
        open={isOpen}
        SlideProps={{ onExited }}
        onClose={handleModalClose}
      >
        {content}
      </Drawer>
    );
  }

  return (
    <SDialog open={isOpen} TransitionProps={{ onExited }} onClose={handleModalClose}>
      {content}
    </SDialog>
  );
};

export const RecordingShareModal = () => {
  const isOpen = useRecordingShareModalStore((state) => state.isOpen);
  const isAutoOpened = useRecordingShareModalStore((state) => state.isAutoOpened);
  const shareUrl = useRecordingShareModalStore((state) => state.shareUrl);
  const recordingId = useRecordingShareModalStore((state) => state.recordingId);
  const onClose = useRecordingShareModalStore((state) => state.close);
  const navigate = useNavigate();
  const isPaidSpace = useHasUnlimitedRecordingMinutes();

  useRecordingShareAutoFetch(recordingId, { enabled: isOpen });

  const shouldOpenPricingRef = useRef(false);

  // Leave for pricing only once the modal has finished closing; switching routes mid-transition
  // left the modal stuck open on top of the pricing page.
  const handleExited = () => {
    if (!shouldOpenPricingRef.current) return;

    shouldOpenPricingRef.current = false;
    navigate(`/${PATHS.pricing}`, {
      state: { path: window.location.pathname, pricingEntryPoint: 'recording_share_upgrade' },
    });
  };

  const handleUpgrade = () => {
    shouldOpenPricingRef.current = true;
    onClose();
  };

  return (
    <RecordingShareModalInner
      copyLink={shareUrl}
      isAutoOpened={isAutoOpened}
      isOpen={isOpen}
      recordingId={recordingId}
      showUpsell={!isPaidSpace && !IS_WHITE_LABEL}
      onClose={onClose}
      onExited={handleExited}
      onUpgrade={handleUpgrade}
    />
  );
};
